import {
  BatchOperation,
  BatchOperationAdd,
} from '@rhybus/api'
import { v4 as uuidv4 } from 'uuid'

import { mpfRListsHttp } from '../../assets/ts/api/mpf-r-lists-http'
import { mpfWhoHttp } from '../../assets/ts/api/mpf-who-http'
import {
  COLUMN_NAMES as COL,
  LIST_NAME as DUEOUT_LIST,
} from '../../assets/ts/data/dueout/dueout'
import { STATUS_CODE } from '../../assets/ts/data/dueout/dueout-status-metadata'
import { MetadataContextInterface } from '../../store/metadata-context-provider'
import {
  generateRandomTrueOrFalse,
  getRandomNumberBetween,
  pickRandomArrayItem,
  randomDateFromNow,
  randomDigitString,
  randomPASCODE,
  randomPhoneNumber
} from './testing'
import TEST_DATA from './testing-testdata'

function createRandomDueOutItem(me, metadata: MetadataContextInterface) {
  const item = {}
  const now = new Date()

  item[COL.DueOutID] = createRandomDueOutID()
  item[COL.DueOutGUID] = uuidv4()
  item[COL.Subject] = pickRandomArrayItem(TEST_DATA.Subject)
  item[COL.Description] = pickRandomArrayItem(TEST_DATA.Description)

  // To-do: link the section, category, subcategory so we're picking a valid
  // set of values.  (A category that belongs to the given section, a
  // subcategory that belongs to that category)
  const subcategory = pickRandomArrayItem(metadata.subcategories)

  const section = pickRandomArrayItem(metadata.sections)
  const category = pickRandomArrayItem(metadata.categories)
  const majcom = pickRandomArrayItem(metadata.majcoms)
  const component = pickRandomArrayItem(metadata.components)
  const status = pickRandomArrayItem(
    metadata.dueOutStatuses.filter((s) => s.StatusCode !== STATUS_CODE.SAVED)
  )

  item[COL.StatusCode] = status.StatusCode
  item[COL.SectionCode] = section.SectionCode
  item[COL.CategoryCode] = category.CategoryCode
  item[COL.SubCategoryCode] = subcategory.SubCategoryCode
  item[COL.PreviousSubCategoryCode] = subcategory.SubCategoryCode
  item[COL.FY] = 2024
  item[COL.Priority] = pickRandomArrayItem(['High', 'Low'])

  item[COL.DueDate] = getRandomDateWithinNextTenDays()
  item[COL.RNLTD] = getRandomDateWithinNextTenDays()
  item[COL.PDD] = getRandomDateWithinNextTenDays()
  item[COL.DOS] = getRandomDateWithinNextTenDays()
  item[COL.PRP] = pickRandomArrayItem(['Yes', 'No'])
  item[COL.IsArchived] = generateRandomTrueOrFalse()
  item[COL.ServicingBy] = item[COL.ServicingBy] = pickRandomArrayItem([
    'MPF',
    'CSS',
  ])
  item[COL.Customer] = me.rid
  item[COL.DoDNumber] = randomDigitString(10)
  item[COL.DutyEmail] = pickRandomArrayItem(TEST_DATA.EmailAddresses)
  item[COL.DutyPhone] = randomPhoneNumber()
  item[COL.CustomerStatus] = 'Civilian'
  item[COL.Rank] = 'Civilian'
  item[COL.OrgMAJCOM] = majcom.MAJCOM
  item[COL.Organization] = pickRandomArrayItem(TEST_DATA.Organizations)
  item[COL.Installation] = pickRandomArrayItem(TEST_DATA.Installations)
  item[COL.PASCODE] = randomPASCODE()
  item[COL.PascodeID] = getRandomNumberBetween(1, 20)
  item[COL.PASCODEServicedBy] = pickRandomArrayItem(['MPF', 'CSS'])
  item[COL.ServicingOrganization] = pickRandomArrayItem(['96 MPF'])
  item[COL.ServicingMAJCOMCode] = getRandomNumberBetween(146, 158)
  item[COL.AssignedComponentCode] = component.ComponentCode

  item[COL.DueDate] = randomDateFromNow(5, 15)
  item[COL.ItemCreated] = randomDateFromNow(30, 0)
  item[COL.ItemModified] = now
  item[COL.ItemCreatedBy] = item[COL.ItemModifiedBy] = me.rid
  item[COL.ItemModifiedBy] = me.rid

  return item
}

function createRandomDueOutID() {
  const prefix = randomDigitString(10)
  const suffix = randomDigitString(7)

  return `${prefix} - ${suffix}`
}

async function createDueOuts(
  totalNumToCreate,
  metadata: MetadataContextInterface
) {
  const start = performance.now()
  const batchSize = 100
  let numCreated = 0

  const who = mpfWhoHttp()
  const me = await who.Me()

  while (numCreated < totalNumToCreate) {
    const numToCreateThisBatch = Math.min(
      batchSize,
      totalNumToCreate - numCreated
    )

    const batch = createOneBatchAddDueOuts(me, numToCreateThisBatch, metadata)
    await batch.execute()

    numCreated += numToCreateThisBatch
    console.log(`Created ${numCreated} / ${totalNumToCreate} due outs`)
  }
  const end = performance.now()

  console.log(
    `Created ${totalNumToCreate} items in ${(end - start) / 1000} sec`
  )
}

function createOneBatchAddDueOuts(
  me,
  batchSize,
  metadata: MetadataContextInterface
) {
  const lists = mpfRListsHttp()

  const batchItems: BatchOperation[] = []
  for (let n = 0; n < batchSize; n++) {
    const item = createRandomDueOutItem(me, metadata)

    batchItems.push({
      data: item,
      list: DUEOUT_LIST,
      op: BatchOperationAdd,
    } as BatchOperation)
  }

  return lists.createBatch(BatchOperationAdd, batchItems)
}

function getRandomDateWithinNextTenDays() {
  const today = new Date()
  const tenDaysLater = new Date().setDate(today.getDate() + 10)
  const randomDate = new Date(
    today.getTime() + Math.random() * (tenDaysLater - today.getTime())
  )
  return randomDate.toISOString()
}

export { createDueOuts }
